import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'
import type { VehicleFilter } from '../contexts/VehicleFilterContext'
import { publicAPI } from '../services/api'

interface WeeklyDataPoint {
  week_number: number
  week_start: string
  week_end: string
  total_count: number
  personbil_count: number
  varebil_count: number
  leasing_count: number
  naerings_leasing_count?: number
  privat_leasing_count?: number
  undefined_leasing_count?: number
  loan_count: number
  naering_count: number
  privat_count: number
}

export interface BrandSeries {
  merke_navn: string
  merke: string
}

interface Props {
  data: WeeklyDataPoint[]
  prevYearData: WeeklyDataPoint[]
  year: number
  vehicleFilter: VehicleFilter
  brandSeries?: BrandSeries[]
}

// Per-brand line colors (up to 5; cycles if more)
const BRAND_COLORS = [
  '#e11d48', // rose-600
  '#7c3aed', // violet-600
  '#059669', // emerald-600
  '#d97706', // amber-600
  '#2563eb', // blue-600
]

// Colors matching the site palette
const COLORS = {
  leasingRate: '#8b5cf6',   // purple  (info)
  loanRate: '#06b6d4',      // cyan
  naeringRate: '#f97316',   // orange  (warning)
  privatRate: '#10b981',    // green   (success)
  personbil: '#0ea5e9',     // blue    (primary)
  varebil: '#64748b',       // slate
  prevPersonbil: '#93c5fd', // light blue (prev year)
  prevVarebil: '#cbd5e1',   // light slate (prev year)
}

const nbNO = (n: number) =>
  n.toLocaleString('nb-NO', { maximumFractionDigits: 0 })

const pctNO = (n: number) =>
  (n * 100).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %'

/** Format yyyy-mm-dd → dd.mm.yyyy */
const fmtDate = (d: string) => {
  const parts = d.split('-')
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d
}

type ChartMode = 'andel' | 'antall' | 'akkumulert' | 'mineMerker'

export default function WeeklyTimeSeriesChart({ data, prevYearData, year, vehicleFilter, brandSeries }: Props) {
  const { t } = useTranslation('components')
  const { isDark } = useTheme()
  const [selectedMode, setChartMode] = useState<ChartMode>('antall')
  // The Mine merker tab exists only while the user has active brands
  const hasBrands = (brandSeries?.length ?? 0) > 0
  const chartMode: ChartMode = selectedMode === 'mineMerker' && !hasBrands ? 'antall' : selectedMode
  const prevYear = year - 1

  // Lazy-load loan counts only when user switches to Andeler tab
  const [loanCounts, setLoanCounts] = useState<Record<number, number>>({})
  const loanFetchedRef = useRef<string | null>(null)

  const fetchLoanCounts = useCallback(async () => {
    const varebilParam = vehicleFilter === 'varebil' ? true : vehicleFilter === 'personbil' ? false : undefined
    const key = `${year}-${vehicleFilter}`
    if (loanFetchedRef.current === key) return
    loanFetchedRef.current = key
    try {
      const res = await publicAPI.getWeeklyLoanCounts({ year, varebil: varebilParam })
      setLoanCounts(res.data.data || {})
    } catch {
      loanFetchedRef.current = null
    }
  }, [year, vehicleFilter])

  useEffect(() => {
    if (chartMode === 'andel') {
      fetchLoanCounts()
    }
  }, [chartMode, fetchLoanCounts])

  // Reset loan cache when year/filter changes
  useEffect(() => {
    loanFetchedRef.current = null
    setLoanCounts({})
  }, [year, vehicleFilter])

  // Determine the cutoff: skip Låneandel for the 2 most recent weeks
  const maxWeek = data.length > 0 ? Math.max(...data.map((d) => d.week_number)) : 0
  const loanCutoffWeek = maxWeek - 2

  // Build a lookup for previous year data by week_number
  const prevByWeek = useMemo(() => {
    const map: Record<number, WeeklyDataPoint> = {}
    for (const d of prevYearData) {
      map[d.week_number] = d
    }
    return map
  }, [prevYearData])

  // Merged chart data with ratios and prev year values
  const chartData = useMemo(() => {
    // Only show weeks up to the current year's latest data week
    const weekNumbers = data.map((d) => d.week_number).sort((a, b) => a - b)

    const currentByWeek: Record<number, WeeklyDataPoint> = {}
    for (const d of data) {
      currentByWeek[d.week_number] = d
    }

    let accPersonbil = 0
    let accVarebil = 0
    let accPrevPersonbil = 0
    let accPrevVarebil = 0

    // Bound here: inside the map below, `t` is the week's total count.
    const weekLabel = (n: number) => t('charts.weekLabel', { n })

    return weekNumbers.map((wn) => {
      const cur = currentByWeek[wn]
      const prev = prevByWeek[wn]
      const t = cur?.total_count || 1

      accPersonbil += cur?.personbil_count || 0
      accVarebil += cur?.varebil_count || 0
      accPrevPersonbil += prev?.personbil_count || 0
      accPrevVarebil += prev?.varebil_count || 0

      // Carry over brand_* keys from input data
      const brandKeys: Record<string, number> = {}
      if (cur) {
        for (const [k, v] of Object.entries(cur)) {
          if (k.startsWith('brand_')) brandKeys[k] = (v as number) || 0
        }
      }

      return {
        week_number: wn,
        label: weekLabel(wn),
        week_start: cur?.week_start || prev?.week_start || '',
        week_end: cur?.week_end || prev?.week_end || '',
        total_count: cur?.total_count || 0,
        // Current year counts
        personbil_count: cur?.personbil_count || 0,
        varebil_count: cur?.varebil_count || 0,
        // Ratios
        leasing_rate: cur ? (cur.leasing_count / t) : null,
        loan_rate: cur && wn <= loanCutoffWeek && loanCounts[wn] ? (loanCounts[wn] / t) : null,
        naering_rate: cur ? (cur.naering_count / t) : null,
        privat_rate: cur ? (cur.privat_count / t) : null,
        // Previous year counts
        prev_personbil_count: prev?.personbil_count ?? null,
        prev_varebil_count: prev?.varebil_count ?? null,
        prev_total_count: prev?.total_count ?? null,
        // Accumulated
        acc_personbil: accPersonbil,
        acc_varebil: accVarebil,
        acc_prev_personbil: accPrevPersonbil > 0 ? accPrevPersonbil : null,
        acc_prev_varebil: accPrevVarebil > 0 ? accPrevVarebil : null,
        // Per-brand counts (Mine Merker)
        ...brandKeys,
      }
    })
  }, [data, prevYearData, prevByWeek, loanCutoffWeek, loanCounts, t])

  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null

    const raw = payload[0]?.payload as any

    return (
      <div className="rounded-lg shadow-lg border p-3 text-xs bg-white dark:bg-dark-surface dark:border-dark-border max-w-xs">
        <p className="font-semibold mb-1.5 text-gray-900 dark:text-gray-100">
          {label} ({fmtDate(raw.week_start)} – {fmtDate(raw.week_end)})
        </p>
        {(chartMode === 'andel' || chartMode === 'antall') && raw.total_count > 0 && (
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {t('weeklyChart.totalYear', { year, n: nbNO(raw.total_count) })}
          </p>
        )}
        <div className="space-y-1">
          {/* Andel mode */}
          {chartMode === 'andel' && (
          <>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.leasingRate }}>● {t('weeklyChart.leasingShare')}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{raw.leasing_rate != null ? pctNO(raw.leasing_rate) : '–'}</span>
          </div>
          {raw.loan_rate !== null && (
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.loanRate }}>● {t('weeklyChart.loanShare')}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{pctNO(raw.loan_rate)}</span>
          </div>
          )}
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.naeringRate }}>● {t('weeklyChart.businessShare')}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{raw.naering_rate != null ? pctNO(raw.naering_rate) : '–'}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.privatRate }}>● {t('weeklyChart.privateShare')}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{raw.privat_rate != null ? pctNO(raw.privat_rate) : '–'}</span>
          </div>
          </>
          )}

          {/* Antall mode */}
          {chartMode === 'antall' && (
          <>
          {vehicleFilter !== 'varebil' && (
          <>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.personbil }}>● {t('weeklyChart.personbilerYear', { year })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.personbil_count)}</span>
          </div>
          {raw.prev_personbil_count != null && (
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.prevPersonbil }}>● {t('weeklyChart.personbilerYear', { year: prevYear })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.prev_personbil_count)}</span>
          </div>
          )}
          </>
          )}
          {vehicleFilter !== 'personbil' && (
          <>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.varebil }}>● {t('weeklyChart.varebilerYear', { year })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.varebil_count)}</span>
          </div>
          {raw.prev_varebil_count != null && (
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.prevVarebil }}>● {t('weeklyChart.varebilerYear', { year: prevYear })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.prev_varebil_count)}</span>
          </div>
          )}
          </>
          )}
          </>
          )}

          {/* Per-brand counts from Mine Merker */}
          {(chartMode === 'antall' || chartMode === 'mineMerker') && brandSeries?.map((brand, i) => {
            const val = raw[`brand_${brand.merke_navn}`]
            if (val == null) return null
            return (
            <div key={brand.merke_navn} className="flex justify-between gap-4">
              <span style={{ color: BRAND_COLORS[i % BRAND_COLORS.length] }}>● {brand.merke_navn}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(val)}</span>
            </div>
            )
          })}

          {/* Akkumulert mode */}
          {chartMode === 'akkumulert' && (
          <>
          {vehicleFilter !== 'varebil' && (
          <>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.personbil }}>● {t('weeklyChart.personbilerYear', { year })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.acc_personbil)}</span>
          </div>
          {raw.acc_prev_personbil != null && (
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.prevPersonbil }}>● {t('weeklyChart.personbilerYear', { year: prevYear })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.acc_prev_personbil)}</span>
          </div>
          )}
          </>
          )}
          {vehicleFilter !== 'personbil' && (
          <>
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.varebil }}>● {t('weeklyChart.varebilerYear', { year })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.acc_varebil)}</span>
          </div>
          {raw.acc_prev_varebil != null && (
          <div className="flex justify-between gap-4">
            <span style={{ color: COLORS.prevVarebil }}>● {t('weeklyChart.varebilerYear', { year: prevYear })}</span>
            <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.acc_prev_varebil)}</span>
          </div>
          )}
          </>
          )}
          </>
          )}
        </div>
      </div>
    )
  }

  const subtitle: Record<ChartMode, string> = {
    andel: t('weeklyChart.subtitleShare'),
    antall: t('weeklyChart.subtitleCount'),
    akkumulert: t('weeklyChart.subtitleAccumulated', { year, prevYear }),
    mineMerker: t('weeklyChart.subtitleMineMerker'),
  }

  const modeLabel: Record<ChartMode, string> = {
    andel: t('weeklyChart.modeShare'),
    antall: t('weeklyChart.modeCount'),
    akkumulert: t('weeklyChart.modeAccumulated'),
    mineMerker: t('weeklyChart.modeMineMerker'),
  }
  const modes: ChartMode[] = hasBrands
    ? ['andel', 'antall', 'akkumulert', 'mineMerker']
    : ['andel', 'antall', 'akkumulert']

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('weeklyChart.title', { year })}
        </h2>
        <div className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          {modes.map((mode) => (
            <button
              key={mode}
              onClick={() => setChartMode(mode)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                chartMode === mode
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
              }`}
            >
              {modeLabel[mode]}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {subtitle[chartMode]}
      </p>

      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />

          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            interval={Math.max(0, Math.floor(chartData.length / 13) - 1)}
          />

          {/* Left Y-axis: ratios 0-1 */}
          {chartMode === 'andel' && (
          <YAxis
            yAxisId="ratio"
            orientation="left"
            domain={[0, 1]}
            tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            label={{
              value: t('charts.share'),
              angle: -90,
              position: 'insideLeft',
              style: { fill: axisColor, fontSize: 12 },
            }}
          />
          )}

          {/* Right Y-axis: absolute count (antall + akkumulert + mine merker) */}
          {chartMode !== 'andel' && (
          <YAxis
            yAxisId="count"
            orientation="right"
            domain={[0, 'auto']}
            tickFormatter={(v: number) => nbNO(v)}
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            label={{
              value: chartMode === 'akkumulert' ? t('charts.accumulated') : t('charts.count'),
              angle: 90,
              position: 'insideRight',
              style: { fill: axisColor, fontSize: 12 },
            }}
          />
          )}

          <Tooltip content={<CustomTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: 12, color: axisColor }}
            iconType="circle"
          />

          {/* ── Andel mode lines ── */}
          {chartMode === 'andel' && (
          <>
          <Line
            yAxisId="ratio"
            type="monotone"
            dataKey="leasing_rate"
            name={t('weeklyChart.leasingShare')}
            stroke={COLORS.leasingRate}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="ratio"
            type="monotone"
            dataKey="loan_rate"
            name={t('weeklyChart.loanShare')}
            stroke={COLORS.loanRate}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="ratio"
            type="monotone"
            dataKey="naering_rate"
            name={t('weeklyChart.businessShare')}
            stroke={COLORS.naeringRate}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          <Line
            yAxisId="ratio"
            type="monotone"
            dataKey="privat_rate"
            name={t('weeklyChart.privateShare')}
            stroke={COLORS.privatRate}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          </>
          )}

          {/* ── Antall mode lines ── */}
          {chartMode === 'antall' && (
          <>
          {/* Current year – solid lines */}
          {vehicleFilter !== 'varebil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="personbil_count"
            name={t('weeklyChart.personbilerYear', { year })}
            stroke={COLORS.personbil}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          )}
          {vehicleFilter !== 'personbil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="varebil_count"
            name={t('weeklyChart.varebilerYear', { year })}
            stroke={COLORS.varebil}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          )}
          {/* Previous year – dotted lines */}
          {vehicleFilter !== 'varebil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="prev_personbil_count"
            name={t('weeklyChart.personbilerYear', { year: prevYear })}
            stroke={COLORS.prevPersonbil}
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
          )}
          {vehicleFilter !== 'personbil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="prev_varebil_count"
            name={t('weeklyChart.varebilerYear', { year: prevYear })}
            stroke={COLORS.prevVarebil}
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
          )}
          </>
          )}

          {/* ── Per-brand lines from Mine Merker (Antall + Mine merker) ── */}
          {(chartMode === 'antall' || chartMode === 'mineMerker') && brandSeries?.map((brand, i) => (
          <Line
            key={brand.merke_navn}
            yAxisId="count"
            type="monotone"
            dataKey={`brand_${brand.merke_navn}`}
            name={brand.merke_navn}
            stroke={BRAND_COLORS[i % BRAND_COLORS.length]}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
          ))}

          {/* ── Akkumulert mode lines ── */}
          {chartMode === 'akkumulert' && (
          <>
          {/* Current year – solid lines */}
          {vehicleFilter !== 'varebil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="acc_personbil"
            name={t('weeklyChart.personbilerYear', { year })}
            stroke={COLORS.personbil}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          )}
          {vehicleFilter !== 'personbil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="acc_varebil"
            name={t('weeklyChart.varebilerYear', { year })}
            stroke={COLORS.varebil}
            strokeWidth={2}
            dot={{ r: 2 }}
            activeDot={{ r: 5 }}
          />
          )}
          {/* Previous year – dotted lines */}
          {vehicleFilter !== 'varebil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="acc_prev_personbil"
            name={t('weeklyChart.personbilerYear', { year: prevYear })}
            stroke={COLORS.prevPersonbil}
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
          )}
          {vehicleFilter !== 'personbil' && (
          <Line
            yAxisId="count"
            type="monotone"
            dataKey="acc_prev_varebil"
            name={t('weeklyChart.varebilerYear', { year: prevYear })}
            stroke={COLORS.prevVarebil}
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            activeDot={{ r: 4 }}
            connectNulls={false}
          />
          )}
          </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
